/** Shared layout + icons for features-grid blocks. */

import { escapeHtml } from './helpers';
import {
  FEATURE_ICON_PACK,
  getFeatureIconDef,
  resolveFeatureIconId,
} from './featureIconPack';

export type FeaturesGridLayoutVariant =
  | 'cards'
  | 'icon-2x2'
  | 'icon-list'
  | 'numbered'
  | 'compact-4'
  | 'dark-band';

export const FEATURES_GRID_LAYOUT_OPTIONS: { id: FeaturesGridLayoutVariant; label: string }[] = [
  { id: 'cards', label: 'Cards (3 columns)' },
  { id: 'icon-2x2', label: 'Icon grid (2×2)' },
  { id: 'icon-list', label: 'Icon list (stacked)' },
  { id: 'numbered', label: 'Numbered steps' },
  { id: 'compact-4', label: 'Compact (4 columns)' },
  { id: 'dark-band', label: 'Dark highlight' },
];

const PACK_ICON_VARIANTS = new Set<FeaturesGridLayoutVariant>([
  'icon-2x2',
  'icon-list',
  'numbered',
  'compact-4',
  'dark-band',
]);

/** @deprecated Use FEATURE_ICON_PACK from featureIconPack.ts */
export const FEATURE_ICON_OPTIONS = FEATURE_ICON_PACK.map((icon) => ({
  id: icon.id,
  label: icon.label,
}));

const ICON_COLOR = '#111827';

export type FeatureItemIcon = {
  icon?: string;
  iconUrl?: string;
};

export type FeatureIconDisplay =
  | { kind: 'image'; url: string }
  | { kind: 'pack'; key: string }
  | { kind: 'emoji'; text: string };

export function isImageIconUrl(value?: string): boolean {
  const v = (value || '').trim();
  if (!v) return false;
  return /^(https?:\/\/|data:image\/|\/)/i.test(v);
}

export function isBuiltInIconKey(key?: string): boolean {
  return Boolean(key && resolveFeatureIconId(key));
}

export function resolveFeatureIconDisplay(feature: FeatureItemIcon): FeatureIconDisplay | null {
  const url = (feature.iconUrl || '').trim();
  if (isImageIconUrl(url)) {
    return { kind: 'image', url };
  }
  const icon = (feature.icon || '').trim();
  if (isImageIconUrl(icon)) {
    return { kind: 'image', url: icon };
  }
  if (isBuiltInIconKey(icon)) {
    return { kind: 'pack', key: icon };
  }
  if (icon) {
    return { kind: 'emoji', text: icon };
  }
  return null;
}

export function hasFeatureIcon(feature: FeatureItemIcon): boolean {
  return resolveFeatureIconDisplay(feature) !== null;
}

/** @deprecated Use resolveFeatureIconDisplay */
export function isEmojiOrTextIcon(icon?: string): boolean {
  const display = resolveFeatureIconDisplay({ icon });
  return display?.kind === 'emoji';
}

const LAYOUT_VARIANT_IDS = new Set<string>(FEATURES_GRID_LAYOUT_OPTIONS.map((o) => o.id));

export function resolveFeaturesGridVariant(content: { layoutVariant?: string }): FeaturesGridLayoutVariant {
  const v = content.layoutVariant;
  if (v && LAYOUT_VARIANT_IDS.has(v)) return v as FeaturesGridLayoutVariant;
  return 'cards';
}

export function usesPackIconLayout(variant: FeaturesGridLayoutVariant): boolean {
  return PACK_ICON_VARIANTS.has(variant);
}

export function getFeaturesGridColumns(variant: FeaturesGridLayoutVariant, isMobile: boolean): number {
  if (variant === 'icon-list') return 1;
  if (isMobile) {
    if (variant === 'compact-4') return 2;
    return 1;
  }
  switch (variant) {
    case 'icon-2x2':
    case 'numbered':
      return 2;
    case 'compact-4':
      return 4;
    case 'cards':
    case 'dark-band':
      return 3;
    default:
      return 3;
  }
}

export function renderFeatureIconSvg(iconKey?: string, size = 28): string {
  const def = getFeatureIconDef(iconKey) || getFeatureIconDef('mouse-pointer-2');
  const inner = def?.paths || '';
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${ICON_COLOR}" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;
}

/** HTML for email export (image, pack SVG, or emoji). */
export function renderFeatureIconHtml(feature: FeatureItemIcon, size = 28): string {
  const display = resolveFeatureIconDisplay(feature);
  if (!display) return '';
  if (display.kind === 'image') {
    return `<img src="${escapeHtml(display.url)}" alt="" width="${size}" height="${size}" style="display:block;width:${size}px;height:${size}px;object-fit:contain;" />`;
  }
  if (display.kind === 'pack') {
    return renderFeatureIconSvg(display.key, size);
  }
  return `<div style="font-size: ${Math.round(size * 0.9)}px; line-height: 1;">${escapeHtml(display.text)}</div>`;
}

export const DEFAULT_ICON_2X2_SECTION_TITLE = 'Everything you need in one place';

/** Plain-language sample copy for the 2×2 icon grid (no brand-specific jargon). */
export const DEFAULT_ICON_2X2_FEATURES = [
  {
    icon: 'mail',
    title: 'Create emails easily',
    description: 'Pick a template, add your message, and send—no design or coding skills required.',
    enabled: true,
  },
  {
    icon: 'users',
    title: 'Reach the right people',
    description: 'Organize contacts into lists and send to the audience that matters most.',
    enabled: true,
  },
  {
    icon: 'chart-line',
    title: 'See what works',
    description: 'Check opens and clicks so you know which emails your readers enjoy.',
    enabled: true,
  },
  {
    icon: 'shield-check',
    title: 'Send with confidence',
    description: 'Reliable delivery and support as your subscriber list grows.',
    enabled: true,
  },
];

const LEGACY_ICON_2X2_TITLES = new Set([
  'design & build',
  'publish & edit',
  'analyze & optimize',
  'scale & collaborate',
  'design & send',
  'automate & segment',
  'track & optimize',
  'deliver & scale',
]);

function featureText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/** Detects old Webflow/WXP sample content still stored on saved blocks. */
export function isLegacyIcon2x2FeaturesContent(content: {
  sectionTitle?: string;
  features?: Array<{ title?: string; description?: string }>;
}): boolean {
  const sectionTitle = featureText(content.sectionTitle).toLowerCase();
  if (sectionTitle.includes('wxp') || sectionTitle.includes('webflow')) return true;
  if (sectionTitle.includes("what's possible with scubamail")) return true;

  const features = Array.isArray(content.features) ? content.features : [];
  if (features.some((f) => /webflow/i.test(featureText(f.description)))) return true;
  if (features.some((f) => /html,\s*css,\s*and\s*javascript/i.test(featureText(f.description)))) {
    return true;
  }
  if (features.some((f) => /visual canvas/i.test(featureText(f.description)))) return true;

  const legacyTitleMatches = features.filter((f) =>
    LEGACY_ICON_2X2_TITLES.has(featureText(f.title).toLowerCase())
  ).length;
  return legacyTitleMatches >= 2;
}

/** Returns content with generic sample copy when legacy Webflow/WXP placeholders are detected. */
export function normalizeFeaturesGridContent<T extends Record<string, unknown>>(content: T): T {
  if (resolveFeaturesGridVariant(content) !== 'icon-2x2') return content;
  if (!isLegacyIcon2x2FeaturesContent(content as Parameters<typeof isLegacyIcon2x2FeaturesContent>[0])) {
    return content;
  }

  return {
    ...content,
    sectionTitle: DEFAULT_ICON_2X2_SECTION_TITLE,
    features: DEFAULT_ICON_2X2_FEATURES.map((feature) => ({ ...feature })),
  };
}

export const DEFAULT_ICON_LIST_SECTION_TITLE = 'Why people choose us';
export const DEFAULT_ICON_LIST_FEATURES = [
  {
    icon: 'mail',
    title: 'Simple email creation',
    description: 'Templates and drag-and-drop editing—no technical skills needed.',
    enabled: true,
  },
  {
    icon: 'users',
    title: 'Smart contact lists',
    description: 'Group subscribers and send the right message every time.',
    enabled: true,
  },
  {
    icon: 'chart-line',
    title: 'Clear performance stats',
    description: 'See opens and clicks so you know what resonates.',
    enabled: true,
  },
];

export const DEFAULT_NUMBERED_SECTION_TITLE = 'How it works';
export const DEFAULT_NUMBERED_FEATURES = [
  {
    icon: 'mail-plus',
    title: 'Sign up free',
    description: 'Create your account in minutes and import your contacts.',
    enabled: true,
  },
  {
    icon: 'layout-grid',
    title: 'Design your email',
    description: 'Pick a template, customize your message, and preview on any device.',
    enabled: true,
  },
  {
    icon: 'send',
    title: 'Send to your list',
    description: 'Choose who receives it and hit send with one click.',
    enabled: true,
  },
  {
    icon: 'chart-line',
    title: 'Track results',
    description: 'Review opens and clicks to improve your next campaign.',
    enabled: true,
  },
];

export const DEFAULT_COMPACT_SECTION_TITLE = 'Built for growing teams';
export const DEFAULT_COMPACT_FEATURES = [
  {
    icon: 'zap',
    title: 'Fast setup',
    description: 'Start sending in minutes.',
    enabled: true,
  },
  {
    icon: 'shield-check',
    title: 'Secure',
    description: 'Your data stays protected.',
    enabled: true,
  },
  {
    icon: 'smartphone',
    title: 'Mobile ready',
    description: 'Looks great on every screen.',
    enabled: true,
  },
  {
    icon: 'headphones',
    title: 'Support',
    description: 'Help when you need it.',
    enabled: true,
  },
];

export const DEFAULT_DARK_BAND_SECTION_TITLE = 'Powerful tools, simple to use';
export const DEFAULT_DARK_BAND_FEATURES = [
  {
    icon: 'mail',
    title: 'Beautiful emails',
    description: 'Professional designs without a designer.',
    enabled: true,
  },
  {
    icon: 'workflow',
    title: 'Automation',
    description: 'Welcome series, reminders, and more on autopilot.',
    enabled: true,
  },
  {
    icon: 'chart-bar',
    title: 'Analytics',
    description: 'Know what works with clear reports.',
    enabled: true,
  },
];

export const DEFAULT_CARDS_FEATURES = [
  {
    icon: '⚡',
    title: 'Fast & Reliable',
    description: 'Lightning-fast performance with 99.9% uptime guarantee.',
    enabled: true,
  },
  {
    icon: '🔒',
    title: 'Secure & Private',
    description: 'Enterprise-grade security to protect your data.',
    enabled: true,
  },
  {
    icon: '💡',
    title: 'Easy to Use',
    description: 'Intuitive interface that anyone can master quickly.',
    enabled: true,
  },
];

export function getDefaultFeaturesForLayout(variant: FeaturesGridLayoutVariant): {
  sectionTitle: string;
  features: typeof DEFAULT_ICON_2X2_FEATURES;
} {
  switch (variant) {
    case 'icon-2x2':
      return {
        sectionTitle: DEFAULT_ICON_2X2_SECTION_TITLE,
        features: DEFAULT_ICON_2X2_FEATURES.map((f) => ({ ...f })),
      };
    case 'icon-list':
      return {
        sectionTitle: DEFAULT_ICON_LIST_SECTION_TITLE,
        features: DEFAULT_ICON_LIST_FEATURES.map((f) => ({ ...f })),
      };
    case 'numbered':
      return {
        sectionTitle: DEFAULT_NUMBERED_SECTION_TITLE,
        features: DEFAULT_NUMBERED_FEATURES.map((f) => ({ ...f })),
      };
    case 'compact-4':
      return {
        sectionTitle: DEFAULT_COMPACT_SECTION_TITLE,
        features: DEFAULT_COMPACT_FEATURES.map((f) => ({ ...f })),
      };
    case 'dark-band':
      return {
        sectionTitle: DEFAULT_DARK_BAND_SECTION_TITLE,
        features: DEFAULT_DARK_BAND_FEATURES.map((f) => ({ ...f })),
      };
    case 'cards':
    default:
      return {
        sectionTitle: 'Why Choose Us',
        features: DEFAULT_CARDS_FEATURES.map((f) => ({ ...f })),
      };
  }
}
